import React, { Component } from 'react';
import axios from 'axios';

// Waste extras configuration
const wasteFields = [
    { name: 'clearingFridge', label: 'Fridge/Freezer' },
    { name: 'clearingMattress', label: 'Mattress' },
    { name: 'clearingSofa', label: 'Sofa' },
    { name: 'clearingArmChair', label: 'Arm Chairs' },
    { name: 'clearingOtherPopsWaste', label: 'Other POPs Waste' },
    { name: 'clearingTelevision', label: 'Television/Computer monitor' },
];

// Helper to format HH:MM
function formatTime(value) {
    if (!value) return '';
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toISOString().slice(11, 16);
    return value.slice(0, 5);
}

// Normalize extras into an object with all fields defaulting to 0
function normalizeExtras(extras) {
    const parsed = extras ? (typeof extras === 'string' ? JSON.parse(extras) : extras) : [];
    const normalized = {};
    wasteFields.forEach((f) => {
        const found = parsed.find((e) => e.slug === f.name);
        normalized[f.name] = found ? parseInt(found.quantity, 10) || 0 : 0;
    });
    return normalized;
}

class WasteDetailsForm extends Component {
    constructor(props) {
        super(props);
        const waste = props.waste || {};
        this.state = {
            waste: {
                ...waste,
                entries: (waste.entries || []).map((entry) => ({
                    ...entry,
                    extras: normalizeExtras(entry.extras),
                })),
            },
            saving: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.waste !== this.props.waste) {
            const waste = this.props.waste || {};
            this.setState({
                waste: {
                    ...waste,
                    entries: (waste.entries || []).map((entry) => ({
                        ...entry,
                        extras: normalizeExtras(entry.extras),
                    })),
                },
            });
        }
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState((prev) => ({
            waste: {
                ...prev.waste,
                [name]: value === '' ? null : value,
            },
        }));
    };

    handleEntryChange = (index, field, value) => {
        this.setState((prev) => {
            const entries = [...(prev.waste.entries || [])];
            entries[index] = { ...entries[index], [field]: value === '' ? null : parseInt(value, 10) || 0 };
            return { waste: { ...prev.waste, entries } };
        });
    };

    handleSubmit = async (e) => {
        e.preventDefault();
        const { waste } = this.state;
        this.setState({ saving: true });

        // Convert extras back to array for backend
        const entriesToSend = (waste.entries || []).map((entry) => {
            const extrasArray = Object.keys(entry.extras || {}).map((key) => ({
                slug: key,
                name: wasteFields.find((f) => f.name === key)?.label || key,
                quantity: entry.extras[key] || 0,
            }));
            return { ...entry, extras: extrasArray };
        });

        try {
            await axios.put(`/jobs/waste-tipping/${waste.id}`, { ...waste, entries: entriesToSend });
            alert('Waste details updated successfully');
        } catch (err) {
            console.error(err);
            alert('Failed to save waste details');
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const { waste } = this.state;
        const tipOff = this.props.tipOff || {};
        if (!waste) return null;

        return (
            <section>
                {/* Waste Tipping Info */}
                <div className="row py-4">
                    <div className="col-12">
                        <h6 className="py-2 pl-2 cw-bg-grey text-white">Waste Details</h6>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <p className="mb-0">Where did you tip your waste?</p>
                        <p className="mt-0">{tipOff.name || 'Unknown location'}</p>
                    </div>
                    <div className="col-md-6">
                        <p className="mb-0">Tipping date</p>
                        <p className="mt-0">
                            {waste.tipping_date ? new Date(waste.tipping_date).toLocaleDateString('en-GB') : 'N/A'}
                        </p>
                    </div>
                </div>

                {/* Times */}
                <form onSubmit={this.handleSubmit}>
                    <div className="row mb-3">
                        {['start_tip_off', 'end_tip_off'].map((field, i) => {
                            const labels = ['Tip Off Route Started', 'Tip Off Finished'];
                            return (
                                <div key={field} className="col-md-6">
                                    <div className="form-group">
                                        <label>{labels[i]}</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            name={field}
                                            value={formatTime(waste[field])}
                                            onChange={this.handleChange}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Waste entries */}
                    <div className="row py-4">
                        <div className="col-12">
                            <h6 className="py-2 pl-2 cw-bg-grey text-white">Waste Tipped (per entry)</h6>
                        </div>
                    </div>

                    {(waste.entries || []).map((entry, idx) => {
                        const safeEntry = entry || {};
                        return (
                            <div key={safeEntry.id || idx} className="entry-row mb-3 p-2 border rounded">
                                <h6>Entry {idx + 1}</h6>

                                <div className="row">
                                    {/* Standard waste fields */}
                                    {['wood', 'green_waste', 'general', 'inert'].map((field) => (
                                        <div key={field} className="col-md-3">
                                            <div className="form-group">
                                                <label>
                                                    {field.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    value={safeEntry[field] ?? 0}
                                                    onChange={(e) => this.handleEntryChange(idx, field, e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Extras fields */}
                                <div className="row mt-2">
                                    {wasteFields.map((f) => (
                                        <div key={f.name} className="col-md-2">
                                            <div className="form-group">
                                                <label>{f.label}</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    value={safeEntry.extras[f.name] ?? 0}
                                                    onChange={(e) => {
                                                        const value = parseInt(e.target.value, 10) || 0;
                                                        this.setState((prev) => {
                                                            const entries = [...prev.waste.entries];
                                                            entries[idx].extras = {
                                                                ...entries[idx].extras,
                                                                [f.name]: value,
                                                            };
                                                            return { waste: { ...prev.waste, entries } };
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}

                    <div className="mt-3">
                        <button type="submit" className="btn btn-success btn-sm" disabled={this.state.saving}>
                            {this.state.saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </section>
        );
    }
}

export default WasteDetailsForm;
